/**
 * aminos-subscription-toggle.js
 *
 * Cart-drawer subscription toggle controller. Calls into aminosCartClient
 * for all /cart/*.js mutations — never fetches directly.
 *
 * Uses event delegation on the drawer root so listeners survive any number
 * of Sections API inner-HTML swaps (the live JS rebound directly to toggle
 * elements, which broke after the first refresh).
 *
 * Required DOM contract (see aminos-cart-drawer-subscription-toggle.liquid):
 *   One-time state — opt-in checkbox:
 *     <input type="checkbox" class="subscription-checkbox"
 *            data-line-key data-variant-id data-selling-plan-id>
 *   Subscribed state — revert link:
 *     <button data-aminos-sub-toggle data-subscribe="false"
 *             data-line-key data-variant-id data-selling-plan-id>
 *
 * Uses line item `key` (stable) not line `id` (position-dependent).
 */
(function (global) {
  'use strict';

  const cartClient = global.aminosCartClient;
  if (!cartClient) {
    console.error('[aminos-subscription-toggle] aminos-cart-client.js must load first');
    return;
  }

  /**
   * Run a subscribe/unsubscribe swap for one line item, with a per-item
   * spinner. `control` is the checkbox or button that triggered it; `revert`
   * restores its visual state if the swap fails (the line re-renders on
   * success, so no manual revert is needed then).
   */
  async function runSwap(control, subscribe, revert) {
    const key = control.dataset.lineKey;
    const variantId = Number(control.dataset.variantId);
    const sellingPlanId = Number(control.dataset.sellingPlanId);

    if (!key || !variantId || !sellingPlanId) {
      console.warn('[aminos-subscription-toggle] Missing data attributes', { key, variantId, sellingPlanId });
      return;
    }

    control.disabled = true;
    control.setAttribute('aria-busy', 'true');

    // Spinner over this line item while the price recalculates. A successful
    // swap re-renders the whole line (removing the class); on failure we clear
    // it in the finally block.
    const lineItem = control.closest('.cart-item');
    if (lineItem) lineItem.classList.add('aminos-cart-item--loading');

    try {
      await cartClient.swapSubscription({ key, variantId, sellingPlanId, subscribe });
    } catch (err) {
      console.error('[aminos-subscription-toggle] Swap failed', err);
      if (typeof revert === 'function') revert();
    } finally {
      if (document.body.contains(control)) {
        control.disabled = false;
        control.removeAttribute('aria-busy');
      }
      if (lineItem && document.body.contains(lineItem)) {
        lineItem.classList.remove('aminos-cart-item--loading');
      }
    }
  }

  // One-time → subscribed: opt-in checkbox. 'change' (not 'click') so we read
  // the settled checked state.
  document.body.addEventListener('change', (e) => {
    const box = e.target.closest('.subscription-checkbox');
    if (!box) return;
    const subscribe = box.checked;
    runSwap(box, subscribe, () => {
      box.checked = !subscribe;
    });
  });

  // Subscribed → one-time: "Switch to one-time" link/button.
  document.body.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-aminos-sub-toggle]');
    if (!btn) return;
    e.preventDefault();
    const subscribe = btn.dataset.subscribe === 'true';
    runSwap(btn, subscribe);
  });

  // Disable interaction during mutation across the whole drawer
  document.addEventListener('aminos:cart:mutating', () => {
    document.querySelectorAll('[data-section-id="cart-drawer"]').forEach((el) => {
      el.setAttribute('data-mutating', 'true');
    });
  });

  document.addEventListener('aminos:cart:settled', () => {
    document.querySelectorAll('[data-section-id="cart-drawer"]').forEach((el) => {
      el.removeAttribute('data-mutating');
    });
  });

  document.addEventListener('aminos:cart:error', () => {
    document.querySelectorAll('[data-section-id="cart-drawer"]').forEach((el) => {
      el.removeAttribute('data-mutating');
    });
  });
})(window);
